import React from 'react';
import { Loader } from '@mantine/core';

// /arrow_right_icon_v2.png is black-on-transparent.
// --black-icon-filter keeps it black in light theme and inverts to
// white in dark. direction="left" mirrors the glyph horizontally so
// it points from right to left (still rendered on the right side of
// the button label).
const ArrowIcon = ({ direction = 'right' }) => (
  <img
    src="/arrow_right_icon_v2.png"
    alt=""
    aria-hidden="true"
    style={{
      width: 25.2,
      height: 25.2,
      filter: 'var(--black-icon-filter)',
      transform: direction === 'left' ? 'scaleX(-1)' : undefined,
    }}
  />
);

// Pill-styled action button matching the logbook / paperplane / tag
// pills that sit right above this action strip on ArtPage. Neutral
// gray background, rounded-2xl, tinted colored text, hover flips bg
// to var(--mantine-color-default-hover). Keeps a plain <button>
// underneath so Mantine variants can't override the look.
const PillButton = ({ color = 'blue', loading = false, onClick, children }) => (
  <button
    type="button"
    disabled={loading}
    onClick={onClick}
    className="flex items-center justify-center gap-2 rounded-2xl text-sm font-medium transition-colors cursor-pointer"
    style={{
      height: 39,
      padding: '0 14px',
      background: 'var(--mantine-color-default)',
      color: `var(--mantine-color-${color}-text)`,
      border: 'none',
      opacity: loading ? 0.7 : 1,
      // One-line label so the fixed 39px pill's vertical padding
      // stays visible. Width is dictated by content — no ellipsis;
      // the parent container is sized to the widest sibling so all
      // buttons end up the same width.
      whiteSpace: 'nowrap',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default)'; }}
  >
    {loading && <Loader size="xs" color={color} />}
    <span>{children}</span>
  </button>
);

// Renders the 3-4 action buttons for user(s) selected in the friends
// interaction UI. Shared by the ArtPage inline strip and the
// FollowingDropdown drawer so both surfaces stay in lockstep.
export const renderArtUserActionButtons = ({
  selectedUsers,
  language,
  hasEvent,
  hasRecommendationFromMe,
  hasRecommendationToMe,
  recommendationsFromMeMap,
  recommendationsToMeMap,
  messagesSentToUser,
  sendingToUser,
  navigate,
  handleRecommend,
  navigateToDiscussion,
  sendArtLinkToUser,
  goToDirectChat,
  getUserName,
}) => {
  if (selectedUsers.length === 1) {
    const single = selectedUsers[0];
    return [
      hasEvent(single) && (
        <PillButton
          key="disc"
          color="blue"
          onClick={() => navigateToDiscussion(single)}
        >
          <span className="inline-flex items-center gap-1.5">
            {language === "ru" ? "К обсуждению" : "To discussion"}
            {/* chat_icon moved here from the removed "Send in a message"
                button so this stays the primary chat affordance. 20px
                keeps it inside the 39px pill. */}
            <img
              src="/chat_icon.png"
              alt=""
              aria-hidden="true"
              style={{ width: 20, height: 20, filter: 'var(--icon-filter)' }}
            />
          </span>
        </PillButton>
      ),
      hasRecommendationFromMe(single) && (
        <PillButton
          key="fromMeRec"
          color="blue"
          onClick={() => navigate(`/recommendations/${recommendationsFromMeMap[single]}`)}
        >
          <span className="inline-flex items-center gap-1.5">
            {language === "ru" ? "Перейти к моей рекомендации" : "Go to my recommendation"}
            <ArrowIcon />
          </span>
        </PillButton>
      ),
      hasRecommendationToMe(single) && (() => {
        const name = getUserName?.(single);
        return (
          <PillButton
            key="toMeRec"
            color="blue"
            onClick={() => navigate(`/recommendations/${recommendationsToMeMap[single]}`)}
          >
            <span className="inline-flex items-center gap-1.5">
              {language === "ru"
                ? `Перейти к рекомендации от${name ? ` ${name}` : " пользователя"}`
                : `Go to recommendation from ${name || "user"}`}
              <ArrowIcon direction="left" />
            </span>
          </PillButton>
        );
      })(),
      // 1-way personal recommendations (frontend-only for now): if the
      // other user has already recommended this art TO me, hide the
      // reciprocal "Рекомендовать" so the strip doesn't offer both
      // directions at once.
      !hasRecommendationFromMe(single) && !hasRecommendationToMe(single) && (
        <PillButton
          key="rec"
          color="blue"
          onClick={() => handleRecommend([single])}
        >
          <span className="inline-flex items-center gap-1.5">
            {language === "ru" ? "Рекомендовать" : "Recommend"}
            <ArrowIcon />
          </span>
        </PillButton>
      ),
      // "Отправить в сообщении" removed — the chat affordance now lives
      // on the "К обсуждению" pill above (chat_icon moved there).
    ].filter(Boolean);
  }
  if (
    selectedUsers.length > 1 &&
    selectedUsers.every((userId) => !hasRecommendationFromMe(userId))
  ) {
    return [
      <PillButton
        key="recSep"
        color="blue"
        onClick={() => handleRecommend(selectedUsers)}
      >
        {language === "ru" ? "Рекомендовать отдельно" : "Recommend separately"}
      </PillButton>,
    ];
  }
  return [];
};
